/* eslint-disable no-console -- the error link logs GraphQL and network errors, as the Apollo Client 2 setup did */
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { ServerError } from '@apollo/client/errors';
import { print, type GraphQLFormattedError } from 'graphql';
import get from 'lodash/get';
import { from, map, mergeMap, tap } from 'rxjs';
import { backendErrors as enBackendErrors } from 'i18n/en';
import { backendErrors as deBackendErrors } from 'i18n/de';
import { formatQueryByErrorPositions } from 'utils/helpers';
import isRecord from 'utils/isRecord';
import { afterLogout } from 'hooks/auth/useLogout';
import RefreshTokenService from 'services/RefreshTokenService';
import settings from 'config/settings';
import { LogoutDocument } from 'graphql/generated/graphql';
import possibleTypesResult from './generated/possibleTypes';

/** The logout must not wait for, or start, another logout when its own token refresh fails. */
const LOGOUT_OPERATION = 'Logout';

interface ILoggedError {
  message?: string;
  locations?: GraphQLFormattedError['locations'];
  path?: GraphQLFormattedError['path'];
  extensions?: Record<string, unknown>;
  stack?: string;
}

interface ITranslatedError extends GraphQLFormattedError {
  originalMessage: string;
}

let logoutPromise: Promise<void> | null = null;

/** Ends the session once, even when several operations ask for it at the same time. */
const triggerLogout = (): Promise<void> => {
  if (!logoutPromise) {
    logoutPromise = (async () => {
      // Without a refresh token the server-side logout always fails with
      // 'jwt must be provided', so for a guest the local cleanup is enough.
      if (RefreshTokenService.getRefreshToken()) {
        try {
          await client.mutate({ mutation: LogoutDocument });
        } catch (e) {
          console.log('Error while logout', e);
        }
      }
      afterLogout(client);
    })().finally(() => {
      logoutPromise = null;
    });
  }
  return logoutPromise;
};

/** After a failed refresh: the operations that waited for it go on first, then the session ends. */
const scheduleLogout = () => {
  window.setTimeout(() => {
    window.setTimeout(() => {
      triggerLogout();
    });
  });
};

let refreshPromise: Promise<void> | null = null;

/**
 * Single-flight refresh: operations sent while the access token is expired wait for one `token` request and are
 * sent afterwards, also when the refresh failed (the session then ends, see scheduleLogout).
 */
const runRefresh = (): Promise<void> => {
  if (!refreshPromise) {
    refreshPromise = RefreshTokenService.fetchNewAccessToken()
      .then(() => {
        console.log('refreshed the access token');
      })
      .catch(() => {
        scheduleLogout();
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

/** The server needs a valid access token to end the session; a failed refresh must not start a second logout. */
const refreshWithoutLogout = (): Promise<void> =>
  RefreshTokenService.fetchNewAccessToken().then(
    () => undefined,
    () => undefined,
  );

const authLink = new ApolloLink((operation, forward) => {
  if (RefreshTokenService.isTokenValidOrUndefined()) return forward(operation);
  const refresh =
    operation.operationName === LOGOUT_OPERATION ? refreshWithoutLogout() : runRefresh();
  return from(refresh).pipe(mergeMap(() => forward(operation)));
});

const stacktraceOf = (extensions: Record<string, unknown> | undefined): string => {
  const exception = extensions?.exception;
  if (!isRecord(exception) || !Array.isArray(exception.stacktrace)) return '';
  return exception.stacktrace.join('\n');
};

const extensionsText = (extensions: Record<string, unknown> | undefined): string => {
  const exception = extensions?.exception;
  if (!isRecord(exception)) return JSON.stringify({ ...extensions });
  return JSON.stringify({
    ...extensions,
    exception: { ...exception, ...(exception.stacktrace ? { stacktrace: '(below)' } : {}) },
  });
};

/** The console message of the Apollo Client 2 setup: message, location, path, query with markers, variables. */
const logError = (error: ILoggedError, operation: ApolloLink.Operation, errorType: string) => {
  console.log(error, errorType);
  const { message, locations, path, extensions, stack } = error;
  const exception = extensions?.exception;
  if (isRecord(exception)) exception.logged = true;
  const lStacktrace = stacktraceOf(extensions) || stack || '';
  const lVariables = JSON.stringify(operation.variables);
  const lQuery = formatQueryByErrorPositions({
    queryString: print(operation.query),
    errorPositions: locations,
  });
  const lPath = JSON.stringify(path);
  const lLocations = JSON.stringify(locations);
  const lMessage = JSON.stringify(message);
  const lExtensions = extensionsText(extensions);
  console.error(
    [
      `[${errorType}]:`,
      lMessage && `Message: ${lMessage}`,
      lLocations && `Location: ${lLocations}`,
      lPath && `Path: ${lPath}`,
      lQuery && `Query:\n${lQuery}`,
      lVariables && `Variables: ${lVariables}`,
      lExtensions && `Extensions: ${lExtensions}`,
      lStacktrace && `Stacktrace: ${lStacktrace}`,
    ]
      .filter(Boolean)
      .join('\n'),
    extensions,
  );
};

const doNotLogOnClient = (error: GraphQLFormattedError) =>
  Boolean(get(error, 'extensions.exception.doNotLogOnClient'));

/** Backend error codes become the translation of the active UI language (the original stays in originalMessage). */
const translateError = (error: GraphQLFormattedError): ITranslatedError => {
  const locale = window?.localStorage?.getItem('language') || settings.defaultLocale;
  const backendErrorsTranslations = { de: deBackendErrors, en: enBackendErrors };
  const messageKey = `${locale}.${error.message}`;
  const translated = get(backendErrorsTranslations, messageKey, error.message);
  const message = typeof translated === 'string' ? translated : error.message;
  return { ...error, originalMessage: error.message, message };
};

/** GraphQL errors in the JSON body of a non-2xx response. */
const serverErrorsOf = (error: unknown): GraphQLFormattedError[] | null => {
  if (!ServerError.is(error)) return null;
  try {
    const body: unknown = JSON.parse(error.bodyText);
    if (!isRecord(body) || !Array.isArray(body.errors)) return null;
    return body.errors.filter(
      (item): item is GraphQLFormattedError => isRecord(item) && typeof item.message === 'string',
    );
  } catch {
    return null;
  }
};

const networkErrorOf = (error: unknown): ILoggedError => {
  if (error instanceof Error) return { message: error.message, stack: error.stack };
  return { message: String(error) };
};

const errorLink = new ApolloLink((operation, forward) =>
  forward(operation).pipe(
    // GraphQL errors: translated first (the callers see the translation), then logged, as before.
    map((result) => {
      if (!result.errors) return result;
      const errors = result.errors.map(translateError);
      const code = errors[0]?.extensions?.code;
      if (code === 'FORBIDDEN') {
        window.location.replace(`${window.location.origin}/app/secure/logout`);
      }
      errors.forEach((error) => {
        if (!doNotLogOnClient(error)) logError(error, operation, 'GraphQL error');
      });
      return { ...result, errors };
    }),
    tap({
      error: (error: unknown) => {
        const graphQLErrors = serverErrorsOf(error);
        if (graphQLErrors) {
          graphQLErrors.forEach((graphQLError) => {
            if (!doNotLogOnClient(graphQLError)) logError(graphQLError, operation, 'GraphQL error');
          });
          return;
        }
        logError(networkErrorOf(error), operation, 'Network error');
      },
    }),
  ),
);

const httpLink = new HttpLink({
  uri: settings.graphqlServerUrl,
  credentials: 'include',
});

const client: ApolloClient = new ApolloClient({
  link: ApolloLink.from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache({
    possibleTypes: possibleTypesResult.possibleTypes,
    // The cache keys of the Apollo Client 2 setup: <__typename>___<_id>.
    dataIdFromObject: ({ _id, __typename }) => (_id ? `${__typename}___${_id}` : undefined),
    typePolicies: {
      Project: {
        fields: {
          // These nested objects have no id of their own, and the project queries select different
          // fields of them. Without a merge policy Apollo replaces the cached object with the smaller
          // incoming one and warns that cache data may be lost; merging keeps the fields already known.
          form_values: { merge: true },
          calculations: { merge: true },
          plan: { merge: true },
          progress: { merge: true },
          loan: { merge: true },
          transferredBy: { merge: true },
        },
      },
      // The budget calculation and its items have no id either; the Financing query selects only two
      // items with `calculated_value`. Replacing them left MyBudget incomplete, so it was refetched
      // while the lifestyle calculator remounted its step 4 with empty amounts.
      BudgetCalculatorCalculation: { merge: true },
      BudgetCalculatorCalculationItem: { merge: true },
    },
  }),
  // Apollo Client 2 did not re-render watching components with loading: true while they refetch.
  defaultOptions: { watchQuery: { notifyOnNetworkStatusChange: false } },
  assumeImmutableResults: true,
});

export default client;
